import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Clock } from 'lucide-react';

interface DateSelectorProps {
    onDateChange: (utcDate: string) => void;
}

const SingleDateRangeSelector = ({ onDateChange }: DateSelectorProps) => {
    const [selectedRange, setSelectedRange] = useState('last_24h');
    const [isOpen, setIsOpen] = useState(false);
    const [customDate, setCustomDate] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const calculateUTC = (range: string, manualDate?: string) => {
        const now = new Date();
        let resultDate = new Date();
        if (range === 'last_24h') resultDate.setHours(now.getHours() - 24);
        else if (range === 'last_week') resultDate.setDate(now.getDate() - 7);
        else if (range === 'custom' && manualDate) {
            resultDate = new Date(manualDate);
            resultDate.setUTCHours(0, 0, 0, 0);
        }
        return resultDate.toISOString().split('.')[0];
    };

    const handleSelect = (range: string, date?: string) => {
        setSelectedRange(range);
        if (range !== 'custom' || date) {
            onDateChange(calculateUTC(range, date));
            setIsOpen(false);
        }
    };

    return (
        <div ref={dropdownRef} className="relative inline-block w-28 text-[8px]">
            {/* Compact Button */}
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center justify-between w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 hover:border-orange-500/50 dark:hover:border-orange-500/50 rounded px-2 py-0.5 text-gray-700 dark:text-slate-300 transition-all shadow-sm"
            >
                <div className="flex items-center gap-1.5 truncate text-[9px] font-bold">
                    <Clock className="w-2.5 h-2.5 text-orange-500 flex-shrink-0" />
                    <span className="truncate">
                        {selectedRange === 'custom' && customDate ? customDate : (selectedRange === 'last_24h' ? '24 Hours' : 'Last Week')}
                    </span>
                </div>
                <ChevronDown className={`w-2.5 h-2.5 text-gray-500 dark:text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Compact Dropdown */}
            {isOpen && (
                <div className="absolute right-0 z-50 mt-1 w-36 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-1.5 animate-in fade-in zoom-in duration-150">
                    {/* Horizontal Preset Row */}
                    <div className="flex gap-1 mb-2">
                        <button
                            type="button"
                            onClick={() => handleSelect('last_24h')}
                            className={`flex-1 py-1 rounded text-[9px] font-bold transition-colors ${selectedRange === 'last_24h' ? 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/30' : 'bg-gray-100 dark:bg-slate-700/50 text-gray-600 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-700 hover:text-gray-900 dark:hover:text-slate-200'}`}
                        >
                            24H
                        </button>
                        <button
                            type="button"
                            onClick={() => handleSelect('last_week')}
                            className={`flex-1 py-1 rounded text-[9px] font-bold transition-colors ${selectedRange === 'last_week' ? 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/30' : 'bg-gray-100 dark:bg-slate-700/50 text-gray-600 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-700 hover:text-gray-900 dark:hover:text-slate-200'}`}
                        >
                            1W
                        </button>
                    </div>

                    {/* Custom Input Section */}
                    <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                        <input
                            type="date"
                            className="w-full bg-gray-50 dark:bg-gray-900/60 border border-gray-300 dark:border-gray-600 rounded px-1.5 py-1 text-gray-900 dark:text-slate-200 text-[10px] outline-none mb-2 cursor-pointer dark:[color-scheme:dark]"
                            onChange={(e) => { setCustomDate(e.target.value); setSelectedRange('custom'); }}
                        />
                        <button
                            type="button"
                            onClick={() => handleSelect('custom', customDate)}
                            disabled={!customDate}
                            className="w-full bg-orange-600 hover:bg-orange-500 disabled:opacity-30 text-white text-[9px] font-bold py-1 rounded transition-all uppercase tracking-tight shadow-sm"
                        >
                            Apply
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SingleDateRangeSelector;
